import React, { useEffect, useState, useRef } from "react";
import { Button } from "./components/ui/button";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import CheckList from "@editorjs/checklist";
import NestedList from "@editorjs/nested-list";
import Warning from "@editorjs/warning";
import Table from "@editorjs/table";
import InlineCode from "@editorjs/inline-code";
import { MainWrapper } from "./components/ui/main-wrapper";
import { SidebarMenu } from "./components/ui/sidebar";
import { MainArea } from "./components/ui/main-area";
import { NoteItem } from "./components/ui/note-item";
import { v4 as uuidv4 } from "uuid";
import {
  Download,
  Archive,
  AlertTriangle,
  FileWarningIcon,
} from "lucide-react";
import JSZip from "jszip";
import { cn } from "./components/lib/utils";

let editor = null;
let saveTimeout = null;

async function initializeEditor(content, functionAfterInit) {
  return new Promise((resolve, reject) => {
    if (!editor) {
      editor = new EditorJS({
        holder: "editorjs",
        tools: {
          header: {
            class: Header,
            config: {
              placeholder: "Heading",
            },
            inlineToolbar: true,
          },
          paragraph: {
            class: Paragraph,
            config: {
              placeholder: "",
            },
            inlineToolbar: true,
          },
          list: {
            class: NestedList,
            inlineToolbar: true,
            config: {
              defaultStyle: "ordered",
            },
          },
          quote: {
            class: Quote,
            inlineToolbar: true,
          },
          delimiter: {
            class: Delimiter,
            inlineToolbar: true,
          },
          warning: {
            class: Warning,
            inlineToolbar: true,
          },
          table: {
            class: Table,
            inlineToolbar: true,
          },
          inlineCode: InlineCode,
          checklist: {
            class: CheckList,
            inlineToolbar: true,
          },
        },
        data: content ? content : null,
      });
      if (functionAfterInit) {
        functionAfterInit();
      }
    }
  });
}

// Constants for storage management
const STORAGE_KEYS = {
  NOTES: "notes",
  BACKUP: "notes_backup",
  LAST_BACKUP: "last_backup_time",
  STORAGE_WARNING: "storage_warning_shown",
};

const STORAGE_THRESHOLDS = {
  WARNING: 0.8, // 80% of quota
  CRITICAL: 0.95, // 95% of quota
};

// Utility functions for storage management
const storageUtils = {
  // Check if localStorage is available
  isAvailable: () => {
    try {
      const test = "test";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  // Get estimated storage usage (percentage of quota)
  getStorageUsage: async () => {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        return estimate.usage / estimate.quota;
      } catch (e) {
        console.error("Error estimating storage:", e);
        return null;
      }
    }
    return null;
  },

  // Get total size of localStorage in bytes
  getLocalStorageSize: () => {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      total += (key.length + value.length) * 2; // UTF-16 uses 2 bytes per character
    }
    return total;
  },

  // Format bytes to human-readable format
  formatBytes: (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  },
};

function App() {
  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [storageWarning, setStorageWarning] = useState(null);
  const [storageError, setStorageError] = useState(null);
  const backupIntervalRef = useRef(null);
  const [dialogClosed, setDialogClosed] = useState(false);

  useEffect(() => {
    // Initialize app with error handling
    initializeApp();

    // Set up periodic storage checks and backups
    backupIntervalRef.current = setInterval(
      checkStorageAndBackup,
      5 * 60 * 1000
    ); // Every 5 minutes

    // Clean up on unmount
    return () => {
      if (backupIntervalRef.current) {
        clearInterval(backupIntervalRef.current);
      }
    };
  }, []);

  // Initialize app with storage availability check
  async function initializeApp() {
    if (!storageUtils.isAvailable()) {
      setStorageError(
        "LocalStorage is not available. Your notes cannot be saved."
      );
      return;
    }

    try {
      // Check storage quota
      await checkStorageQuota();

      // Load notes with recovery mechanism
      await loadNotes();
    } catch (error) {
      console.error("Error initializing app:", error);
      setStorageError(
        "Failed to initialize the app. Please try refreshing the page."
      );
    }
  }

  // Check storage quota and show warnings if needed
  async function checkStorageQuota() {
    try {
      const usageRatio = await storageUtils.getStorageUsage();

      // If we can't get the estimate, use a simpler method
      if (usageRatio === null) {
        // Rough estimate based on localStorage size
        const totalSize = storageUtils.getLocalStorageSize();
        const estimatedLimit = 5 * 1024 * 1024; // Assume 5MB limit (common in browsers)
        const estimatedRatio = totalSize / estimatedLimit;

        handleStorageWarning(estimatedRatio, totalSize);
        return;
      }

      handleStorageWarning(usageRatio);
    } catch (error) {
      console.error("Error checking storage quota:", error);
    }
  }

  // Handle storage warnings based on usage ratio
  function handleStorageWarning(usageRatio, size = null) {
    // Clear any existing warnings if usage is back to normal
    if (usageRatio < STORAGE_THRESHOLDS.WARNING) {
      setStorageWarning(null);
      localStorage.removeItem(STORAGE_KEYS.STORAGE_WARNING);
      return;
    }

    // Critical threshold - show error
    if (usageRatio >= STORAGE_THRESHOLDS.CRITICAL) {
      const message = size
        ? `Storage nearly full (${storageUtils.formatBytes(
            size
          )}). You won't be able to save new notes. Please export your data and delete some notes.`
        : `Storage nearly full (${Math.round(
            usageRatio * 100
          )}% used). You won't be able to save new notes. Please export your data and delete some notes.`;

      setStorageWarning({ level: "critical", message });
      localStorage.setItem(
        STORAGE_KEYS.STORAGE_WARNING,
        JSON.stringify({ level: "critical", timestamp: Date.now() })
      );
      return;
    }

    // Warning threshold
    if (usageRatio >= STORAGE_THRESHOLDS.WARNING) {
      const message = size
        ? `Storage usage high (${storageUtils.formatBytes(
            size
          )}). Consider exporting and deleting some notes.`
        : `Storage usage high (${Math.round(
            usageRatio * 100
          )}% used). Consider exporting and deleting some notes.`;

      setStorageWarning({ level: "warning", message });
      localStorage.setItem(
        STORAGE_KEYS.STORAGE_WARNING,
        JSON.stringify({ level: "warning", timestamp: Date.now() })
      );
    }
  }

  // Check storage and create backup if needed
  async function checkStorageAndBackup() {
    try {
      // Check storage quota first
      await checkStorageQuota();

      // Don't attempt backup if we're at critical storage levels
      if (storageWarning && storageWarning.level === "critical") {
        return;
      }

      // Check when last backup was made
      const lastBackupTime = localStorage.getItem(STORAGE_KEYS.LAST_BACKUP);
      const now = Date.now();

      // If no backup exists or it's older than 1 hour, create a new one
      if (!lastBackupTime || now - parseInt(lastBackupTime) > 60 * 60 * 1000) {
        createBackup();
      }
    } catch (error) {
      console.error("Error in storage check and backup:", error);
    }
  }

  // Create a backup of notes
  function createBackup() {
    try {
      const notesJSON = localStorage.getItem(STORAGE_KEYS.NOTES);
      if (notesJSON) {
        localStorage.setItem(STORAGE_KEYS.BACKUP, notesJSON);
        localStorage.setItem(STORAGE_KEYS.LAST_BACKUP, Date.now().toString());
      }
    } catch (error) {
      console.error("Failed to create backup:", error);
    }
  }

  // Get stored notes with fallback to backup if primary storage fails
  function getStoredNotes() {
    try {
      // Try to get notes from primary storage
      const notesJSON = localStorage.getItem(STORAGE_KEYS.NOTES);
      if (notesJSON) {
        return JSON.parse(notesJSON);
      }

      // If primary storage failed, try backup
      const backupJSON = localStorage.getItem(STORAGE_KEYS.BACKUP);
      if (backupJSON) {
        console.log("Recovered notes from backup storage");
        return JSON.parse(backupJSON);
      }

      // If all fails, return empty array
      return [];
    } catch (error) {
      console.error("Error retrieving notes:", error);

      // Last resort - try backup if parsing primary failed
      try {
        const backupJSON = localStorage.getItem(STORAGE_KEYS.BACKUP);
        if (backupJSON) {
          return JSON.parse(backupJSON);
        }
      } catch (backupError) {
        console.error("Backup retrieval also failed:", backupError);
      }

      return [];
    }
  }

  // Save notes with error handling and quota checking
  function saveNotes(notes) {
    try {
      // Don't save if we have a critical storage warning
      if (storageWarning && storageWarning.level === "critical") {
        throw new Error("Storage quota exceeded. Cannot save notes.");
      }

      const notesJSON = JSON.stringify(notes);

      // Try to save to localStorage
      localStorage.setItem(STORAGE_KEYS.NOTES, notesJSON);

      // If successful, update state
      setNotes(notes);
      setStorageError(null);

      // Check if we should create a backup
      const lastBackupTime = localStorage.getItem(STORAGE_KEYS.LAST_BACKUP);
      const now = Date.now();

      if (!lastBackupTime || now - parseInt(lastBackupTime) > 60 * 60 * 1000) {
        createBackup();
      }

      // Check storage quota after saving
      checkStorageQuota();

      return true;
    } catch (error) {
      console.error("Error saving notes:", error);
      setStorageError(
        `Failed to save notes: ${error.message}. Your changes will not be persisted. Please export your data.`
      );
      return false;
    }
  }

  // Load notes with error handling
  async function loadNotes() {
    try {
      const notes = getStoredNotes();
      setNotes(notes);

      // Check for existing storage warnings
      const warningJSON = localStorage.getItem(STORAGE_KEYS.STORAGE_WARNING);
      if (warningJSON) {
        try {
          const warning = JSON.parse(warningJSON);
          // Only show warnings that are less than 24 hours old
          if (Date.now() - warning.timestamp < 24 * 60 * 60 * 1000) {
            await checkStorageQuota(); // Re-check to get the actual message
          } else {
            localStorage.removeItem(STORAGE_KEYS.STORAGE_WARNING);
          }
        } catch (e) {
          localStorage.removeItem(STORAGE_KEYS.STORAGE_WARNING);
        }
      }

      return notes;
    } catch (error) {
      console.error("Error loading notes:", error);
      setStorageError("Failed to load notes. Please try refreshing the page.");
      return [];
    }
  }

  async function handleInput() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Don't even try to save if we have a critical storage issue
    if (storageWarning && storageWarning.level === "critical") {
      setStorageError(
        "Cannot save changes: storage quota exceeded. Please export your data and delete some notes."
      );
      return;
    }

    saveTimeout = setTimeout(async () => {
      if (editor) {
        try {
          const data = await editor.save();
          console.log("Data saved:", data);
          if (selectedNoteId) {
            // Update existing note
            const updatedNotes = notes.map((note) =>
              note.id === selectedNoteId ? { ...note, content: data } : note
            );

            // Try to save notes, handle failure
            const saveSuccessful = saveNotes(updatedNotes);

            // If save failed, don't update the state
            if (!saveSuccessful) {
              // Trigger export option
              handleStorageSaveFailure();
            }
          }
        } catch (error) {
          console.error("Error saving editor data:", error);
          setStorageError(
            `Failed to save changes: ${error.message}. Please export your data.`
          );
        }
      }
    }, 1000); // 1000 milliseconds = 1 second
  }

  // Handle storage save failure
  function handleStorageSaveFailure() {
    // We already set the error message in saveNotes
    // Here we could add additional recovery mechanisms

    // Re-check storage quota
    checkStorageQuota();
  }

  async function handleNoteClick(id) {
    setSelectedNoteId(id);
    const note = notes.find((n) => n.id === id);
    if (note) {
      if (!editor) {
        await initializeEditor(note.content); // Initialize editor if it hasn't been already
        return;
      }
      try {
        await editor.clear(); // Clear current content
        if (note.content?.blocks) {
          await editor.render(note.content); // Render the selected note's content
        }
      } catch (error) {
        console.error("Error rendering note:", error);
      }
    }
  }

  async function handleAddNote() {
    if (!editor) {
      await initializeEditor(null, () => {
        let noteID = uuidv4();
        const newNote = {
          id: noteID,
          title: `Untitled note`,
          content: {},
        };
        const updatedNotes = [...notes, newNote];
        setSelectedNoteId(newNote.id);
        saveNotes(updatedNotes);
        setNotes(updatedNotes);
      });
      return;
    } else {
      editor.clear();
      let noteID = uuidv4();
      const newNote = {
        id: noteID,
        title: `Untitled note`,
        content: {},
      };
      const updatedNotes = [...notes, newNote];
      setSelectedNoteId(newNote.id);
      saveNotes(updatedNotes);
      setNotes(updatedNotes);
    }
  }

  // Convert EditorJS content to Markdown format
  function convertToMarkdown(editorData) {
    if (!editorData || !editorData.blocks || editorData.blocks.length === 0) {
      return "";
    }

    let markdown = "";

    editorData.blocks.forEach((block) => {
      switch (block.type) {
        case "header":
          const headerLevel = "#".repeat(block.data.level);
          markdown += `${headerLevel} ${block.data.text}\n\n`;
          break;
        case "paragraph":
          markdown += `${block.data.text}\n\n`;
          break;
        case "list":
          if (block.data.style === "ordered") {
            block.data.items.forEach((item, index) => {
              markdown += `${index + 1}. ${item}\n`;
            });
          } else {
            block.data.items.forEach((item) => {
              markdown += `- ${item}\n`;
            });
          }
          markdown += "\n";
          break;
        case "checklist":
          block.data.items.forEach((item) => {
            const checkbox = item.checked ? "[x]" : "[ ]";
            markdown += `${checkbox} ${item.text}\n`;
          });
          markdown += "\n";
          break;
        case "quote":
          markdown += `> ${block.data.text}\n`;
          if (block.data.caption) {
            markdown += `> — ${block.data.caption}\n`;
          }
          markdown += "\n";
          break;
        case "delimiter":
          markdown += "---\n\n";
          break;
        case "warning":
          markdown += `**⚠️ ${block.data.title || "Warning"}**\n\n${
            block.data.message
          }\n\n`;
          break;
        case "table":
          if (block.data.content && block.data.content.length > 0) {
            // Create header row
            const headerRow = block.data.content[0];
            markdown += "|";
            headerRow.forEach(() => {
              markdown += " --- |";
            });
            markdown += "\n";

            // Create data rows
            block.data.content.forEach((row) => {
              markdown += "|";
              row.forEach((cell) => {
                markdown += ` ${cell} |`;
              });
              markdown += "\n";
            });
            markdown += "\n";
          }
          break;
        case "inlineCode":
          markdown += `\`${block.data.code}\`\n\n`;
          break;
        default:
          // For unsupported blocks, try to extract text if available
          if (block.data && block.data.text) {
            markdown += `${block.data.text}\n\n`;
          }
      }
    });

    return markdown;
  }

  // Export the current note as markdown
  async function handleExportMarkdown() {
    if (!editor || !selectedNoteId) return;

    try {
      // Always get the latest content from the editor, even if it couldn't be saved to localStorage
      const data = await editor.save();
      const markdown = convertToMarkdown(data);
      const note = notes.find((n) => n.id === selectedNoteId);
      const filename = `${note?.title || "Untitled"}.md`;

      // Create a blob and download link
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      // Clear any storage errors after successful export
      if (storageError) {
        setStorageError(null);
      }
    } catch (error) {
      console.error("Error exporting markdown:", error);
      setStorageError(
        `Failed to export note: ${error.message}. Please try again.`
      );
    }
  }

  // Export all notes as a zip file
  async function handleExportAllNotes() {
    if (notes.length === 0) return;

    try {
      // Create a new zip file
      const zip = new JSZip();
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const zipFilename = `notebomb-export-${timestamp}.zip`;

      // Get the latest notes from localStorage
      const latestNotes = getStoredNotes();
      let untitledIncrement = 0;

      // If there's an active editor with unsaved changes, we need to capture that content
      let currentEditorContent = null;
      let currentNoteId = null;

      if (editor && selectedNoteId) {
        try {
          // Get the current editor content even if it couldn't be saved to localStorage
          currentEditorContent = await editor.save();
          currentNoteId = selectedNoteId;
        } catch (error) {
          console.error("Error getting current editor content:", error);
        }
      }

      // Process each note, with special handling for the currently edited note
      for (const note of latestNotes) {
        try {
          let contentToExport;

          // If this is the currently edited note and we have unsaved changes, use those
          if (note.id === currentNoteId && currentEditorContent) {
            contentToExport = currentEditorContent;
          } else {
            contentToExport = note.content;
          }

          // Convert to markdown
          const markdown = convertToMarkdown(contentToExport);
          let filename;

          if (note.title != "Untitled note") {
            filename = `${note.title}.md`;
          } else {
            filename = `Untitled${
              untitledIncrement == 0 ? "" : ` ${untitledIncrement}`
            }.md`;
            untitledIncrement++;
          }

          // Add to zip
          zip.file(filename, markdown);
        } catch (error) {
          console.error(`Error processing note ${note.id}:`, error);
          // Continue with other notes even if one fails
        }
      }

      // Generate the zip file
      const content = await zip.generateAsync({ type: "blob" });

      // Download the zip file
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      // Clear any storage errors after successful export
      if (storageError) {
        setStorageError(null);
      }
    } catch (error) {
      console.error("Error exporting all notes:", error);
      setStorageError(
        `Failed to export notes: ${error.message}. Please try again.`
      );
    }
  }

  return (
    <MainWrapper>
      {/* Storage warnings and errors */}
      {(storageWarning || storageError) && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg max-w-md ${
            storageWarning?.level === "critical" || storageError
              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">
                {storageError || storageWarning?.message}
              </p>
              {(storageWarning?.level === "critical" || storageError) && (
                <div className="mt-2">
                  <Button
                    onClick={handleExportAllNotes}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 bg-white dark:bg-gray-800"
                  >
                    <Download className="h-4 w-4" />
                    Export All Notes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SidebarMenu exportAllNotes={handleExportAllNotes}>
        <h2>Notes</h2>
        <ul>
          {notes.map((note) => (
            <li key={note.id}>
              <NoteItem
                variant="outline"
                onClick={() => handleNoteClick(note.id)}
                className={
                  selectedNoteId === note.id
                    ? "bg-indigo-200 dark:bg-zinc-700 selected"
                    : ""
                }
              >
                {note.title}
              </NoteItem>
            </li>
          ))}
          <li>
            <NoteItem
              variant="outline"
              disabled={true}
              className={
                storageWarning?.level === "critical"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            >
              Add note (unavailable)
            </NoteItem>
          </li>
        </ul>
      </SidebarMenu>
      <MainArea>
        <div className="flex justify-end mb-4">
          {selectedNoteId && (
            <Button
              onClick={handleExportMarkdown}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              Export as Markdown
            </Button>
          )}
        </div>
        <div id="editorjs" onInput={handleInput}></div>
      </MainArea>
      <dialog
        aria-describedby="dialog-desc"
        aria-modal="true"
        className={cn(
          "flex flex-col gap-4 justify-center m-0 w-screen h-screen items-center fixed inset-0 bg-black/30",
          dialogClosed ? "hidden" : ""
        )}
      >
        <div className="flex flex-col gap-4 justify-center items-center bg-white p-6 rounded-2xl max-h-[90vh] overflow-y-auto">
          <FileWarningIcon className="size-40" />
          <p
            className="text-lg sm:text-3xl font-medium max-w-[500px] px-4"
            id="dialog-desc"
          >
            Notebomb is shutting down and will be unavailable on 8/24/2026. This
            date may not be fully accurate due to time zone differences and
            potential delays. To avoid any data loss, please EXPORT your data as
            soon as possible using our tool. You'll get markdown files you can
            import into most note-taking apps. In the time being, you will not
            be able to create any new notes.
          </p>
          <Button
            onClick={() => setDialogClosed(true)}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 bg-white dark:bg-gray-800"
          >
            Okay, dismiss
          </Button>
        </div>
      </dialog>
    </MainWrapper>
  );
}

export default App;
